import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ApexLineChart } from "@xfrontend/cycraft-chart";
import {
  CraftyRobot,
  Closed,
  Delete,
  Edit,
  List,
  Defend,
  Launch,
} from "@xfrontend/xicon";
import {
  Accordion, Button, Dropdown, Spinner,
} from "@xfrontend/xui-react-plus";
import { ServiceTokenType } from "#/apis/infra/getServiceTokens";
import { POLICY_TAG_COLOR_INDEX } from "#/types/xecguard";
import RiskLevelTag from "#/modules/common/RiskLevelTag";
import ViolationPolicyTypeTag from "#/modules/common/ViolationPolicyTypeTag";
import { useLanguage } from "#/i18n/LanguageContext";
import { getPolicyLocale } from "#/i18n/policyTranslations";
import { usePolicyTag } from "#/i18n/hooks";
import ReactScrollbarsCustom from "#/modules/common/ReactScrollbarsCustom";
import { PolicyItem } from "../types";

const RightPanel = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const RightPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetailTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--x-gray-200);
`;

const DetailDescription = styled.div`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--x-gray-300, #a9a9ac);
`;

const InfoCard = styled.div`
  background: var(--x-purple-700, #2C2835);
  border-radius: 8px;
  padding: 12px;
`;

const ChartPlaceholder = styled.div`
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  min-height: 220px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 0;
`;

const SectionTitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const SectionIcon = styled.div`
  padding: 5px;
  background: linear-gradient(
    180deg,
    var(--x-purple-700, #2c2835) 0%,
    var(--x-purple-900, #0f0d12) 100%
  );
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CustomRulesAccordion = styled(Accordion)`
  .accordion-item {
    border: 0;
    border-radius: 0;
    overflow: hidden;
  }

  .accordion-button {
    background: var(--x-purple-500, #494358);
    border: 0;
    border-bottom: 1px solid var(--x-purple-400, #6a6280);
    box-shadow: none;
    height: 45px;
    padding: 0 12px;
    border-radius: 0;
    color: var(--x-gray-200, #f6f6f7);
    font-size: 14px;
    line-height: 1.5;
    &::after {
      margin-left: 9px;
    }
  }

  .accordion-item:not(:first-of-type) .accordion-button {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .accordion-item:not(:last-of-type) .accordion-button {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .accordion-body {
    background: var(--x-purple-600, #3a3646);
    border-bottom: 1px solid var(--x-purple-400, #6a6280);
    padding: 12px;
  }

  .accordion-item:last-of-type .accordion-body {
    border-bottom: 0;
  }
`;

const CustomRulesAccordionWrap = styled.div`
  width: 100%;
  background: var(--x-purple-600, #3a3646);
  border-radius: 4px;
  overflow: hidden;
`;

const AccordionHeaderRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: 100%;
`;

const AccordionTitle = styled.span`
  flex: 1;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 1.5;
`;

const RuleMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const RuleCount = styled.span`
  font-size: 12px;
  line-height: 1;
  color: var(--x-gray-200, #f6f6f7);
`;

const RuleMenuToggle = styled((
  props: React.ComponentProps<typeof Dropdown.Toggle>,
) => (
  <Dropdown.Toggle {...props} variant="secondary" size="sm" />
))`
  background: var(--x-theme-secondary, #73698a);
  border: 2px solid var(--x-theme-secondary, #73698a);
  border-radius: 2px;
  padding: 6px 12px;
  font-size: 12px;
  line-height: 1;
`;

const RuleMenu = styled(Dropdown.Menu)`
  z-index: 2000;
`;

const MarkdownContainer = styled.div`
  position: relative;
  background: var(--x-purple-700, #2c2835);
  border-radius: 4px;
  padding: 9px;
  display: flex;
  gap: 6px;
  min-height: 160px;
`;

const MarkdownText = styled.pre`
  flex: 1;
  margin: 0;
  font-family: "Roboto Mono", monospace;
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-300, #e9e9eb);
  white-space: pre-wrap;
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-radius: 4px;
  border: 1px solid var(--x-purple-500, #494358);
`;

const TokenInfo = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 12px;
`;

const TokenIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
`;

const TokenText = styled.div`
  display: flex;
  flex-direction: column;
`;

const TokenTitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TokenTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
  line-height: 1.5;
`;

const TokenSubtitle = styled.div`
  font-size: 14px;
  color: var(--x-gray-500, #a9a9ac);
  line-height: 1.5;
`;

const TokenStatusBadge = styled.span<{ status: "Enabled" | "Disabled" }>`
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;
  color: var(--x-gray-200);
  background: ${(props: { status: "Enabled" | "Disabled" }) => {
    if (props.status === "Enabled") {
      return "var(--x-theme-success, #00AAB6)";
    }
    return "var(--x-theme-secondary, #73698a)";
  }};
`;

const ViolationCard = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  height: 30px;
  padding: 6px;
  border-radius: 2px;
  background: var(--x-purple-700, #2c2835);
`;

const ViolationLabel = styled.div`
  font-size: 14px;
  color: var(--x-gray-400, #cfd0d3);
  line-height: 1.5;
`;

const ViolationValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-red-light, #ffa6a6);
  line-height: 1.5;
`;

const TokenActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const LaunchButton = styled.button`
  border: 0;
  background: transparent;
  padding: 2px;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SubtleText = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 12px;
`;

const SectionDescription = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
`;

const PatternRulesCard = styled(InfoCard)`
  padding: 18px;
`;

const PatternLead = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
  margin-top: 12px;
`;

const PatternDescription = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
  margin-top: 0;
`;

const PatternInputWrap = styled.div`
  margin-top: 18px;
`;

const PatternInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
`;

const PatternField = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const PatternFieldLabel = styled.div`
  font-size: 14px;
  line-height: 1;
  color: var(--x-gray-200, #f6f6f7);
`;

const PatternRequiredMark = styled.span`
  color: var(--x-red, #ff595a);
`;

const PatternInputBox = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 2px;
  border: 2px solid var(--x-theme-secondary, #73698a);
  border-radius: 4px;
`;

const PatternInput = styled.input`
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1;
  padding: 0 12px;
  outline: none;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }
`;

const PatternAddButton = styled.button`
  min-width: 63px;
  height: 32px;
  border: 2px solid var(--x-red, #ff595a);
  border-radius: 4px;
  background: var(--x-red, #ff595a);
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1;
  padding: 0 16px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PatternLimitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
`;

const PatternRulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow: hidden;
  border-radius: 4px;
  margin-top: 12px;
`;

const PatternRuleRow = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--x-purple-500, #3b3649);
  border-bottom: 1px solid var(--x-purple-800, #1d1b23);
  padding-left: 9px;

  &:last-child {
    border-bottom: 0;
  }
`;

const PatternRuleValue = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.5;
`;

const PatternDeleteButton = styled.button`
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  color: var(--x-gray-400, #cfd0d3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    color: var(--x-red, #ff595a);
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const PatternEmptyState = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 9px;
  background: var(--x-purple-500, #3b3649);
  color: var(--x-gray-500, #a9a9ac);
  font-size: 14px;
  line-height: 1.5;
`;

const PolicyInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const PolicyInfoLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const PolicyInfoField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PolicyInfoLabel = styled.span`
  font-size: 14px;
  color: var(--x-gray-400, #cfd0d3);
  white-space: nowrap;
`;

const PolicyMetaCard = styled.div`
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`;

const MetaLabel = styled.span`
  font-size: 13px;
  color: var(--x-gray-400, #cfd0d3);
  white-space: nowrap;
  min-width: 100px;
`;

const MetaValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--x-gray-200, #f6f6f7);
  font-family: "Roboto Mono", monospace;
`;

/* eslint-disable @typescript-eslint/naming-convention */
type PoliciesRightPanelProps = {
  selectedPolicy?: PolicyItem;
  serviceTokens?: ServiceTokenType[];
  policyType?: string;
  rulesList?: string[];
  rulesLoading?: boolean;
  onEditPolicy?: () => void;
  onCreateRule?: () => void;
  onEditRuleStrings?: (index: number) => void;
  onAddPatternRule?: (value: string) => boolean;
  onDeleteRule?: (index: number) => void;
  onDeletePolicy?: () => void;
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatCreatedTime = (dateStr: string, naFallback: string) => {
  if (!dateStr) return naFallback;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return naFallback;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const getRulesSectionCopy = (t: TFunction, policyType?: string) => {
  if (policyType === "pattern_keyword") {
    return {
      title: t("rightPanel.rules.keywordTitle"),
      description: t("rightPanel.rules.keywordDescription"),
      itemTitle: t("rightPanel.rules.keywordItemTitle"),
      icon: Closed,
    };
  }

  if (policyType === "pattern_regex") {
    return {
      title: t("rightPanel.rules.regexTitle"),
      description: t("rightPanel.rules.regexDescription"),
      itemTitle: t("rightPanel.rules.regexItemTitle"),
      icon: Closed,
    };
  }

  return {
    title: t("rightPanel.rules.customTitle"),
    description: t("rightPanel.rules.customDescription"),
    itemTitle: t("rightPanel.rules.customItemTitle"),
    icon: List,
  };
};

const getPatternRulesCopy = (t: TFunction, policyType?: string) => {
  if (policyType === "pattern_keyword") {
    return {
      lead: t("rightPanel.pattern.keywordLead"),
      description: t("rightPanel.pattern.keywordDescription"),
      inputLabel: t("rightPanel.pattern.keywordInputLabel"),
      placeholder: t("rightPanel.pattern.keywordPlaceholder"),
      limitLabel: t("rightPanel.pattern.keywordLimitLabel"),
      emptyState: t("rightPanel.pattern.keywordEmptyState"),
    };
  }

  return {
    lead: t("rightPanel.pattern.regexLead"),
    description: t("rightPanel.pattern.regexDescription"),
    inputLabel: t("rightPanel.pattern.regexInputLabel"),
    placeholder: t("rightPanel.pattern.regexPlaceholder"),
    limitLabel: t("rightPanel.pattern.regexLimitLabel"),
    emptyState: t("rightPanel.pattern.regexEmptyState"),
  };
};

const PoliciesRightPanel = ({
  selectedPolicy,
  serviceTokens = [],
  policyType,
  rulesList = [],
  rulesLoading = false,
  onEditPolicy,
  onCreateRule,
  onEditRuleStrings,
  onAddPatternRule,
  onDeleteRule,
  onDeletePolicy,
}: PoliciesRightPanelProps): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation("policySpec");
  const { t: tCommon } = useTranslation("common");
  const policyTagInfo = usePolicyTag(selectedPolicy?.policyName ?? "");
  const { language } = useLanguage();
  const [patternInputValue, setPatternInputValue] = useState("");
  const daywiseData = selectedPolicy?.daywiseData ?? [];
  const tokenwiseData = selectedPolicy?.tokenwiseData ?? [];

  const translated = selectedPolicy?.policyName
    ? getPolicyLocale(language, selectedPolicy.policyName, {
      name: selectedPolicy?.name ?? "",
      description: selectedPolicy?.description ?? "",
    })
    : { name: selectedPolicy?.name ?? "", description: selectedPolicy?.description ?? "" };
  const translatedName = translated.name;
  const translatedDescription = translated.description;

  const threatTrends = useMemo(() => {
    const categories = daywiseData.map((d) => formatDate(d.date));
    const unsafeData = daywiseData.map((d) => d.request_unsafe_count);
    const totalData = daywiseData.map((d) => d.request_count);
    const maxVal = Math.max(...totalData, ...unsafeData, 10);
    const roundedMax = Math.ceil(maxVal / 5) * 5;

    return {
      series: [
        { name: tCommon("chartSeries.totalRequests"), type: "line", data: totalData },
        { name: tCommon("chartSeries.policyViolations"), type: "column", data: unsafeData },
      ],
      colors: [
        "var(--x-red-light, #ffa6a6)",
        "var(--x-theme-warning, #846CE6)",
      ],
      categories,
      config: {
        amountAxis: {
          min: 0,
          max: roundedMax,
          tickAmount: 5,
        },
      },
    };
  }, [daywiseData]);
  const isPolicyTypePending = selectedPolicy?.tag !== "Default" && policyType === undefined;
  const isPatternPolicy = policyType === "pattern_keyword" || policyType === "pattern_regex";
  const rulesSectionCopy = getRulesSectionCopy(t, policyType);
  const patternRulesCopy = isPatternPolicy ? getPatternRulesCopy(t, policyType) : null;
  const RulesSectionIcon = rulesSectionCopy.icon;
  const customRules = rulesList.map((rule, index) => ({
    id: String(index),
    title: t("rightPanel.rules.indexed", { title: rulesSectionCopy.itemTitle, num: index + 1 }),
    description: rule,
  }));
  const patternRuleItems = useMemo(() => {
    const duplicates = new Map<string, number>();

    return rulesList.map((rule) => {
      const duplicateCount = (duplicates.get(rule) ?? 0) + 1;
      duplicates.set(rule, duplicateCount);

      return {
        key: `${rule}-${duplicateCount}`,
        value: rule,
      };
    });
  }, [rulesList]);
  const isPatternAddDisabled = !patternInputValue.trim() || rulesLoading || rulesList.length >= 10;

  useEffect(() => {
    setPatternInputValue("");
  }, [selectedPolicy?.id, policyType]);

  const handleAddPatternRule = () => {
    if (!patternInputValue.trim()) return;
    const didAdd = onAddPatternRule?.(patternInputValue);
    if (didAdd !== false) {
      setPatternInputValue("");
    }
  };

  let rulesSectionCard: React.ReactNode = null;

  if (selectedPolicy?.tag !== "Default") {
    if (isPolicyTypePending) {
      rulesSectionCard = (
        <InfoCard>
          <div style={{
            display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 0",
          }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        </InfoCard>
      );
    } else if (isPatternPolicy && patternRulesCopy) {
      let patternRulesContent: React.ReactNode = (
        <PatternEmptyState>{patternRulesCopy.emptyState}</PatternEmptyState>
      );

      if (rulesLoading) {
        patternRulesContent = (
          <PatternEmptyState>
            <Spinner animation="border" size="sm" variant="primary" />
          </PatternEmptyState>
        );
      } else if (patternRuleItems.length > 0) {
        patternRulesContent = patternRuleItems.map(({ key, value }, index) => (
          <PatternRuleRow key={key}>
            <PatternRuleValue>{value}</PatternRuleValue>
            <PatternDeleteButton
              type="button"
              aria-label={t("rightPanel.pattern.deleteAria", { kind: patternRulesCopy.inputLabel })}
              onClick={() => onDeleteRule?.(index)}
              disabled={rulesLoading}
            >
              <Delete size={14} color="currentColor" />
            </PatternDeleteButton>
          </PatternRuleRow>
        ));
      }

      rulesSectionCard = (
        <PatternRulesCard>
          <SectionTitleRow style={{ marginBottom: 0 }}>
            <SectionIcon>
              <RulesSectionIcon size={14} color="var(--x-gray-200, #F6F6F7)" />
            </SectionIcon>
            <SectionTitle>{rulesSectionCopy.title}</SectionTitle>
          </SectionTitleRow>
          <PatternLead>{patternRulesCopy.lead}</PatternLead>
          <PatternDescription>{patternRulesCopy.description}</PatternDescription>
          <PatternInputWrap>
            <PatternInputRow>
              <PatternField>
                <PatternFieldLabel>
                  {patternRulesCopy.inputLabel}
                  <PatternRequiredMark>*</PatternRequiredMark>
                </PatternFieldLabel>
                <PatternInputBox>
                  <PatternInput
                    value={patternInputValue}
                    onChange={(e) => setPatternInputValue(e.target.value)}
                    placeholder={patternRulesCopy.placeholder}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddPatternRule();
                      }
                    }}
                  />
                </PatternInputBox>
              </PatternField>
              <PatternAddButton
                type="button"
                onClick={handleAddPatternRule}
                disabled={isPatternAddDisabled}
              >
                {t("rightPanel.pattern.addButton")}
              </PatternAddButton>
            </PatternInputRow>
            <PatternLimitRow>
              <span>{patternRulesCopy.limitLabel}</span>
              <span>{`${rulesList.length}/10`}</span>
            </PatternLimitRow>
            <PatternRulesList>{patternRulesContent}</PatternRulesList>
          </PatternInputWrap>
        </PatternRulesCard>
      );
    } else {
      rulesSectionCard = (
        <InfoCard>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8,
          }}
          >
            <SectionTitleRow style={{ marginBottom: 0 }}>
              <SectionIcon>
                <RulesSectionIcon size={14} color="var(--x-gray-200, #F6F6F7)" />
              </SectionIcon>
              <SectionTitle>{rulesSectionCopy.title}</SectionTitle>
            </SectionTitleRow>
            <Button variant="primary" onClick={onCreateRule} disabled={rulesLoading || rulesList.length >= 3}>
              {t("rightPanel.rules.createButton")}
            </Button>
          </div>
          <SubtleText>{rulesSectionCopy.description}</SubtleText>
          {rulesLoading ? (
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 0",
            }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <CustomRulesAccordionWrap>
              <CustomRulesAccordion defaultActiveKey={[]} alwaysOpen>
                {customRules.map((rule, index) => (
                  <Accordion.Item eventKey={rule.id} key={rule.id}>
                    <Accordion.Header>
                      <AccordionHeaderRow>
                        <AccordionTitle>{rule.title}</AccordionTitle>
                      </AccordionHeaderRow>
                    </Accordion.Header>
                    <Accordion.Body>
                      <RuleMetaRow>
                        <RuleCount>
                          {rule.description.length}
                          /400
                        </RuleCount>
                        <Dropdown align="end" container={document.body}>
                          <RuleMenuToggle>{isPatternPolicy ? t("rightPanel.rules.manageRule") : t("rightPanel.rules.editRule")}</RuleMenuToggle>
                          <RuleMenu>
                            {!isPatternPolicy && (
                              <Dropdown.Item onClick={() => onEditRuleStrings?.(index)}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <Edit size={14} color="var(--x-gray-200, #F6F6F7)" />
                                  <span>{t("rightPanel.rules.editStrings")}</span>
                                </div>
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item onClick={() => onDeleteRule?.(index)}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <Delete size={14} color="var(--x-gray-200, #F6F6F7)" />
                                <span>{t("rightPanel.rules.delete")}</span>
                              </div>
                            </Dropdown.Item>
                          </RuleMenu>
                        </Dropdown>
                      </RuleMetaRow>
                      <MarkdownContainer>
                        <MarkdownText>{rule.description}</MarkdownText>
                      </MarkdownContainer>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </CustomRulesAccordion>
            </CustomRulesAccordionWrap>
          )}
        </InfoCard>
      );
    }
  }

  return (
    <RightPanel>
      <ReactScrollbarsCustom>
        <RightPanelInner>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <DetailTitle>{translatedName}</DetailTitle>
            {selectedPolicy?.tag !== "Default" && (
              <Dropdown align="end">
                <Dropdown.Toggle variant="secondary" size="small">
                  {t("rightPanel.modify.button")}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={onEditPolicy}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Edit size={14} color="var(--x-gray-200, #F6F6F7)" />
                      <span>{t("rightPanel.modify.editProperties")}</span>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={onDeletePolicy}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Delete size={14} color="var(--x-gray-200, #F6F6F7)" />
                      <span>{t("rightPanel.modify.deletePolicy")}</span>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
          <InfoCard>
            <PolicyInfoRow>
              <PolicyInfoLeft>
                <PolicyInfoField>
                  <PolicyInfoLabel>{t("rightPanel.info.policyType")}</PolicyInfoLabel>
                  <ViolationPolicyTypeTag index={policyTagInfo.tagIndex}>
                    {policyTagInfo.label || selectedPolicy?.tag || t("rightPanel.info.fallbackTag")}
                  </ViolationPolicyTypeTag>
                  <PolicyInfoLabel style={{ marginLeft: 16 }}>{t("rightPanel.info.riskLevel")}</PolicyInfoLabel>
                  <RiskLevelTag level={selectedPolicy?.riskLevel ?? 1} />
                </PolicyInfoField>
                <DetailDescription>
                  {translatedDescription}
                </DetailDescription>
              </PolicyInfoLeft>
              <PolicyMetaCard>
                <MetaItem>
                  <MetaLabel>{t("rightPanel.info.policyVersion")}</MetaLabel>
                  <MetaValue>{selectedPolicy?.currentVersionId || t("rightPanel.info.naValue")}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>{t("rightPanel.info.policyId")}</MetaLabel>
                  <MetaValue>{selectedPolicy?.id || t("rightPanel.info.naValue")}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>{t("rightPanel.info.createdTime")}</MetaLabel>
                  <MetaValue>{formatCreatedTime(selectedPolicy?.createdAt ?? "", t("rightPanel.info.naValue"))}</MetaValue>
                </MetaItem>
              </PolicyMetaCard>
            </PolicyInfoRow>
            <ChartPlaceholder>
              <div className="position-absolute top-0 bottom-0 start-0 end-0 overflow-hidden">
                <ApexLineChart
                  height="100%"
                  seriesData={threatTrends.series}
                  colors={threatTrends.colors}
                  xaxis={{
                    categories: threatTrends.categories,
                    tickPlacement: "on",
                    crosshairs: { show: false },
                    tooltip: { enabled: false },
                    axisTicks: { show: false },
                    axisBorder: { enabled: false },
                    labels: {
                      style: {
                        colors: "var(--x-gray-400)",
                        fontFamily: "var(--bs-body-font-family)",
                      },
                    },
                  }}
                  yaxis={[
                    {
                      seriesName: [
                        threatTrends.series[0].name,
                        threatTrends.series[1].name,
                      ],
                      min: threatTrends.config.amountAxis.min,
                      max: threatTrends.config.amountAxis.max,
                      tickAmount: threatTrends.config.amountAxis.tickAmount,
                      labels: {
                        style: { colors: "var(--x-gray-400)" },
                        formatter: (val: number) => {
                          if (typeof val === "number") {
                            return val.toFixed(0);
                          }
                          return "";
                        },
                      },
                    },
                  ]}
                  legend={{
                    markers: {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      shape: ["line", "circle"] as any,
                      size: [7, 5],
                      radius: 10,
                      customHTML: undefined,
                      onClick: undefined,
                      strokeWidth: [2, 0],
                      offsetX: -5,
                    },
                    itemMargin: {
                      horizontal: 12,
                    },
                  }}
                  plotOptions={{
                    bar: {
                      columnWidth: "60%",
                    },
                  }}
                  grid={{
                    padding: {
                      right: 30,
                      left: 30,
                    },
                    borderColor: "var(--x-purple-500)",
                    strokeDashArray: 0,
                  }}
                  withLegend
                />
              </div>
            </ChartPlaceholder>
          </InfoCard>

          {rulesSectionCard}

          <InfoCard>
            <SectionTitleRow>
              <SectionIcon>
                <Defend size={14} color="var(--x-gray-200, #F6F6F7)" />
              </SectionIcon>
              <SectionTitle>{t("rightPanel.tokens.title")}</SectionTitle>
            </SectionTitleRow>
            <SectionDescription>
              {t("rightPanel.tokens.description")}
            </SectionDescription>
            <div style={{ height: 12 }} />
            {tokenwiseData.map((tw, index) => {
              const matched = serviceTokens.find((st) => st.service_token_name === tw.service_token_name);
              const status = matched?.service_token_status === "enable" ? t("rightPanel.tokens.statusEnabled") : t("rightPanel.tokens.statusDisabled");
              return (
                <React.Fragment key={tw.service_token_name}>
                  {index > 0 && <div style={{ height: 6 }} />}
                  <TokenRow>
                    <TokenInfo>
                      <TokenIcon>
                        <CraftyRobot size={14} color="var(--x-gray-200, #F6F6F7)" />
                      </TokenIcon>
                      <TokenText>
                        <TokenTitleRow>
                          <TokenTitle>{tw.service_token_name}</TokenTitle>
                          {matched && <TokenStatusBadge status={status}>{status}</TokenStatusBadge>}
                        </TokenTitleRow>
                        <TokenSubtitle>{tw.division}</TokenSubtitle>
                      </TokenText>
                    </TokenInfo>
                    <TokenActions>
                      <ViolationCard>
                        <ViolationLabel>{t("rightPanel.tokens.violations")}</ViolationLabel>
                        <ViolationValue>{tw.request_unsafe_count}</ViolationValue>
                      </ViolationCard>
                      {matched && (
                        <LaunchButton
                          type="button"
                          aria-label={t("rightPanel.tokens.openApp")}
                          onClick={() => navigate(`/applications/${matched.service_token_id}`)}
                        >
                          <Launch size={14} color="var(--x-gray-200, #F6F6F7)" />
                        </LaunchButton>
                      )}
                    </TokenActions>
                  </TokenRow>
                </React.Fragment>
              );
            })}
          </InfoCard>
        </RightPanelInner>
      </ReactScrollbarsCustom>
    </RightPanel>
  );
};

PoliciesRightPanel.defaultProps = {
  selectedPolicy: undefined,
  serviceTokens: [],
  policyType: undefined,
  rulesList: [],
  rulesLoading: false,
  onEditPolicy: undefined,
  onCreateRule: undefined,
  onEditRuleStrings: undefined,
  onAddPatternRule: undefined,
  onDeleteRule: undefined,
  onDeletePolicy: undefined,
};

export default PoliciesRightPanel;
